import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Plus, Search, Download, Eye, Trash2, Send,
  DollarSign, Clock, CheckCircle2, XCircle, AlertCircle, Loader2,
  FileText, Building2, Calendar, X, ChevronRight, Printer, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ['EUR', 'USD', 'GBP', 'AED', 'TRY', 'CHF'];
const TAX_RATES = [
  { label: 'Vergi Yok (0%)', value: 0 },
  { label: '5%', value: 0.05 }, { label: '8%', value: 0.08 },
  { label: '10%', value: 0.10 }, { label: '18%', value: 0.18 },
  { label: '20%', value: 0.20 },
];

const STATUS_CONFIG = {
  draft:     { label: 'Taslak',    color: 'bg-slate-100 text-slate-600',     icon: FileText     },
  sent:      { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700',      icon: Send         },
  paid:      { label: 'Ödendi',    color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  overdue:   { label: 'Gecikmiş', color: 'bg-red-100 text-red-700',          icon: AlertCircle  },
  cancelled: { label: 'İptal',    color: 'bg-gray-100 text-gray-500',        icon: XCircle      },
};

const fmt = (v, currency = 'EUR') => {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

const EMPTY_ITEM = { description: '', quantity: 1, unit_price: 0 };

const downloadPDF = (inv) => {
  const doc = new jsPDF();
  const primary  = [124, 58, 237];  // purple-700
  const accent   = [139, 92, 246];  // purple-500
  const gray     = [71, 85, 105];
  const lightGray = [248, 250, 252];

  doc.setFillColor(...primary);
  doc.rect(0, 0, 210, 36, 'F');
  doc.setFillColor(...accent);
  doc.rect(0, 32, 210, 4, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPO STAND', 14, 16);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Supplier Portal', 14, 24);

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 196, 16, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(inv.invoice_number || '', 196, 24, { align: 'right' });

  if (inv.status === 'paid') {
    doc.setTextColor(16, 185, 129, 0.08);
    doc.setFontSize(60);
    doc.setFont('helvetica', 'bold');
    doc.text('PAID', 105, 160, { align: 'center', angle: 45 });
  }
  doc.setTextColor(0, 0, 0);

  doc.setFillColor(...lightGray);
  doc.roundedRect(14, 44, 85, 38, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text('BILLED TO', 19, 52);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(inv.client_name || 'Müşteri', 19, 59);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  doc.text(inv.project_title || '', 19, 65);

  doc.setFillColor(...lightGray);
  doc.roundedRect(110, 44, 85, 38, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text('FATURA BİLGİLERİ', 115, 52);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  doc.text('Proje:', 115, 59);
  doc.text(inv.project_title || '—', 192, 59, { align: 'right', maxWidth: 60 });
  doc.text('Para Birimi:', 115, 71);
  doc.text(inv.currency || 'EUR', 192, 71, { align: 'right' });
  doc.text('Vade:', 115, 77);
  doc.text(fmtDate(inv.due_date), 192, 77, { align: 'right' });

  const sColors = { draft: [100, 116, 139], sent: [59, 130, 246], paid: [16, 185, 129], overdue: [239, 68, 68], cancelled: [156, 163, 175] };
  const sc = sColors[inv.status] || sColors.draft;
  doc.setFillColor(...sc);
  doc.roundedRect(14, 87, 36, 9, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text((inv.status || 'draft').toUpperCase(), 32, 93, { align: 'center' });

  doc.setTextColor(0, 0, 0);
  autoTable(doc, {
    startY: 102,
    head: [['Açıklama', 'Miktar', 'Birim Fiyat', 'Toplam']],
    body: (inv.items || []).map(i => [
      i.description || '',
      i.quantity ?? 1,
      `${inv.currency} ${Number(i.unit_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `${inv.currency} ${Number(i.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    ]),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: lightGray },
    columnStyles: {
      0: { cellWidth: 95 },
      1: { halign: 'center', cellWidth: 20 },
      2: { halign: 'right', cellWidth: 40 },
      3: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFillColor(...lightGray);
  doc.roundedRect(120, finalY, 75, inv.tax_rate > 0 ? 36 : 26, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text('Ara Toplam:', 128, finalY + 9);
  doc.setTextColor(30, 41, 59);
  doc.text(`${inv.currency} ${Number(inv.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 192, finalY + 9, { align: 'right' });
  let yOff = 15;
  if (inv.tax_rate > 0) {
    doc.setTextColor(...gray);
    doc.text(`Vergi (${(inv.tax_rate * 100).toFixed(0)}%):`, 128, finalY + yOff);
    doc.setTextColor(30, 41, 59);
    doc.text(`${inv.currency} ${Number(inv.tax_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 192, finalY + yOff, { align: 'right' });
    yOff += 6;
  }
  doc.setDrawColor(...primary);
  doc.line(128, finalY + yOff + 2, 192, finalY + yOff + 2);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...primary);
  doc.text('TOPLAM', 128, finalY + yOff + 10);
  doc.setFontSize(12);
  doc.text(`${inv.currency} ${Number(inv.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 192, finalY + yOff + 10, { align: 'right' });

  if (inv.notes) {
    const notesY = finalY + (inv.tax_rate > 0 ? 50 : 42);
    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text('Notlar:', 14, notesY);
    doc.setTextColor(30, 41, 59);
    doc.text(inv.notes, 14, notesY + 5, { maxWidth: 180 });
  }

  doc.save(`${inv.invoice_number || 'invoice'}.pdf`);
};

const PAGE_SIZE = 10;

export default function SupplierInvoices() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientOrgs, setClientOrgs]     = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [preview, setPreview]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [updating, setUpdating]     = useState(null);

  const [form, setForm] = useState({
    client_org_id: '', client_name: '', project_title: '',
    currency: 'EUR', tax_rate: 0, due_date: '', notes: '',
    items: [{ ...EMPTY_ITEM }],
  });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${API}/supplier/invoices?${params}`, { headers });
      setInvoices(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Faturalar yüklenemedi'); }
    finally { setLoading(false); }
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/supplier/client-orgs`, { headers })
      .then(r => setClientOrgs(r.data?.orgs || []))
      .catch(() => {});
  }, [token]);

  const handleOrgSelect = (orgId) => {
    const org = clientOrgs.find(o => o.id === orgId);
    setForm(f => ({ ...f, client_org_id: orgId, client_name: org?.display_name || '' }));
  };

  const subtotal     = form.items.reduce((s, i) => s + (parseFloat(i.quantity || 1) * parseFloat(i.unit_price || 0)), 0);
  const tax_amount   = subtotal * form.tax_rate;
  const total_amount = subtotal + tax_amount;

  const setItem = (idx, field, val) => {
    setForm(f => {
      const items = f.items.map((it, i) => i === idx ? { ...it, [field]: val } : it);
      return { ...f, items };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_name) { toast.error('Müşteri seçin'); return; }
    if (form.items.some(i => !i.description)) { toast.error('Tüm kalemlere açıklama girin'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(i => ({
          description: i.description,
          quantity:    parseFloat(i.quantity) || 1,
          unit_price:  parseFloat(i.unit_price) || 0,
          total:       (parseFloat(i.quantity) || 1) * (parseFloat(i.unit_price) || 0),
        })),
        subtotal, tax_amount, total_amount,
      };
      const res = await axios.post(`${API}/supplier/invoices`, payload, { headers });
      setInvoices(prev => [res.data, ...prev]);
      setTotal(t => t + 1);
      setShowCreate(false);
      setForm({ client_org_id: '', client_name: '', project_title: '', currency: 'EUR', tax_rate: 0, due_date: '', notes: '', items: [{ ...EMPTY_ITEM }] });
      toast.success('Fatura oluşturuldu');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fatura oluşturulamadı');
    } finally { setSaving(false); }
  };

  const handleStatus = async (invId, newStatus) => {
    setUpdating(invId);
    try {
      await axios.patch(`${API}/invoices/${invId}/status`, { status: newStatus }, { headers });
      setInvoices(prev => prev.map(i => i.id === invId ? { ...i, status: newStatus } : i));
      toast.success('Durum güncellendi');
    } catch { toast.error('Durum güncellenemedi'); }
    finally { setUpdating(null); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm('Bu taslak faturayı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`, { headers });
      setInvoices(prev => prev.filter(i => i.id !== inv.id));
      setTotal(t => t - 1);
      toast.success('Fatura silindi');
    } catch { toast.error('Fatura silinemedi'); }
  };

  const filtered = invoices.filter(i => {
    const q = search.toLowerCase();
    return !q || (i.invoice_number || '').toLowerCase().includes(q) ||
      (i.client_name || '').toLowerCase().includes(q) ||
      (i.project_title || '').toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // KPI stats
  const totalAmt  = invoices.reduce((s, i) => s + (i.total_amount || 0), 0);
  const paidCount = invoices.filter(i => i.status === 'paid').length;
  const overdueCount = invoices.filter(i => i.status === 'overdue').length;
  const draftCount = invoices.filter(i => i.status === 'draft').length;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900">Faturalar</h1>
          <p className="text-sm text-gray-500">Müşterilerinize fatura oluşturun ve yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />Yenile
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="new-invoice-btn"
            className="gap-2 bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="h-4 w-4" />Yeni Fatura
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam Tutar',   value: fmt(totalAmt, 'EUR'),   icon: DollarSign,  color: 'bg-purple-50 border-purple-100', textColor: 'text-purple-700' },
          { label: 'Ödendi',         value: paidCount,              icon: CheckCircle2,color: 'bg-emerald-50 border-emerald-100', textColor: 'text-emerald-700' },
          { label: 'Gecikmiş',       value: overdueCount,           icon: AlertCircle, color: 'bg-red-50 border-red-100', textColor: 'text-red-600' },
          { label: 'Taslak',         value: draftCount,             icon: FileText,    color: 'bg-slate-50 border-slate-100', textColor: 'text-slate-600' },
        ].map(k => (
          <div key={k.label} className={`border rounded-xl p-4 ${k.color}`}>
            <div className="flex items-center gap-2 mb-1">
              <k.icon className={`h-4 w-4 ${k.textColor}`} />
              <span className="text-xs font-medium text-gray-500">{k.label}</span>
            </div>
            <p className={`text-xl font-black ${k.textColor}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-48 max-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Ara..." className="pl-9 h-9 text-sm" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="invoice-search-input" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 border rounded-lg px-3 text-sm bg-white" data-testid="status-filter">
          <option value="all">Tüm Durumlar</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="no-invoices-message">
          <Receipt className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Fatura bulunamadı</p>
          <p className="text-xs mt-1">İlk faturanızı oluşturmak için yukarıdaki butona tıklayın</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="invoice-list">
          {filtered.map(inv => (
            <div key={inv.id} data-testid={`invoice-row-${inv.id}`}
              className="border border-gray-100 rounded-xl bg-white p-4 flex items-center gap-3 hover:shadow-sm transition-shadow">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                <Receipt className="h-4 w-4 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-gray-900 font-mono">{inv.invoice_number}</span>
                  <StatusBadge status={inv.status} />
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {inv.client_name || '—'}
                  {inv.project_title && <span className="text-gray-400"> · {inv.project_title}</span>}
                </p>
                {inv.due_date && (
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />Vade: {fmtDate(inv.due_date)}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-base font-black text-gray-900">{fmt(inv.total_amount, inv.currency)}</p>
                <p className="text-xs text-gray-400">{inv.currency}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setPreview(inv)} title="Önizle"
                  className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500">
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => downloadPDF(inv)} title="PDF İndir"
                  data-testid={`download-pdf-${inv.id}`}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500">
                  <Download className="h-3.5 w-3.5" />
                </button>
                {inv.status === 'draft' && (
                  <>
                    <button onClick={() => handleStatus(inv.id, 'sent')} title="Gönder"
                      disabled={updating === inv.id}
                      className="h-8 w-8 flex items-center justify-center rounded-lg border border-blue-200 hover:bg-blue-50 text-blue-600">
                      {updating === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => handleDelete(inv)} title="Sil"
                      className="h-8 w-8 flex items-center justify-center rounded-lg border border-red-200 hover:bg-red-50 text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                {inv.status === 'sent' && (
                  <button onClick={() => handleStatus(inv.id, 'paid')} title="Ödendi Olarak İşaretle"
                    disabled={updating === inv.id}
                    className="h-8 px-2 flex items-center gap-1 rounded-lg border border-emerald-200 hover:bg-emerald-50 text-emerald-600 text-xs font-semibold">
                    {updating === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Ödendi
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-gray-500">{total} fatura</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</Button>
            <span className="h-8 px-3 flex items-center text-xs text-gray-600">{page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>→</Button>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-purple-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Yeni Fatura Oluştur</h2>
              </div>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-5">
              {/* Client & Project */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Müşteri *</label>
                  {clientOrgs.length > 0 ? (
                    <select value={form.client_org_id}
                      onChange={e => handleOrgSelect(e.target.value)}
                      className="w-full h-9 border rounded-lg px-3 text-sm bg-white"
                      data-testid="client-org-select">
                      <option value="">Müşteri Seçin</option>
                      {clientOrgs.map(o => (
                        <option key={o.id} value={o.id}>{o.display_name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input placeholder="Müşteri adı" value={form.client_name}
                      onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                      className="h-9 text-sm" data-testid="client-name-input" />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Proje / Konu</label>
                  <Input placeholder="Sipariş veya proje adı" value={form.project_title}
                    onChange={e => setForm(f => ({ ...f, project_title: e.target.value }))}
                    className="h-9 text-sm" data-testid="project-title-input" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Para Birimi</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full h-9 border rounded-lg px-3 text-sm bg-white">
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Vergi Oranı</label>
                  <select value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) }))}
                    className="w-full h-9 border rounded-lg px-3 text-sm bg-white">
                    {TAX_RATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Son Ödeme Tarihi</label>
                  <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                    className="h-9 text-sm" data-testid="due-date-input" />
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-600">Kalemler *</label>
                  <button type="button" onClick={() => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }))}
                    className="text-xs text-purple-600 hover:underline font-medium">+ Kalem Ekle</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <Input placeholder="Açıklama" value={item.description}
                        onChange={e => setItem(idx, 'description', e.target.value)}
                        className="flex-1 h-9 text-sm" required />
                      <Input type="number" placeholder="Miktar" min="1" value={item.quantity}
                        onChange={e => setItem(idx, 'quantity', e.target.value)}
                        className="w-20 h-9 text-sm" />
                      <Input type="number" placeholder="Birim Fiyat" min="0" value={item.unit_price}
                        onChange={e => setItem(idx, 'unit_price', e.target.value)}
                        className="w-32 h-9 text-sm" />
                      {form.items.length > 1 && (
                        <button type="button" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                          className="h-9 w-9 flex items-center justify-center text-red-400 hover:text-red-600 shrink-0">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notlar</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm resize-none h-20 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ödeme koşulları veya notlar..." />
              </div>

              {/* Totals preview */}
              <div className="bg-purple-50 rounded-xl p-4 text-sm space-y-1.5">
                <div className="flex justify-between text-gray-600">
                  <span>Ara Toplam</span>
                  <span>{fmt(subtotal, form.currency)}</span>
                </div>
                {form.tax_rate > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Vergi ({(form.tax_rate * 100).toFixed(0)}%)</span>
                    <span>{fmt(tax_amount, form.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-black text-purple-700 text-base border-t border-purple-200 pt-1.5">
                  <span>TOPLAM</span>
                  <span>{fmt(total_amount, form.currency)}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCreate(false)}>İptal</Button>
                <Button type="submit" disabled={saving} data-testid="save-invoice-btn"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                  Fatura Oluştur
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b">
              <h2 className="text-lg font-bold">Fatura Önizleme</h2>
              <button onClick={() => setPreview(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Fatura No</p>
                  <p className="text-lg font-black font-mono text-purple-700">{preview.invoice_number}</p>
                </div>
                <StatusBadge status={preview.status} />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-400">Müşteri</p>
                  <p className="font-semibold">{preview.client_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Proje</p>
                  <p className="font-semibold">{preview.project_title || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Vade</p>
                  <p className="font-semibold">{fmtDate(preview.due_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Toplam</p>
                  <p className="text-xl font-black text-purple-700">{fmt(preview.total_amount, preview.currency)}</p>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-purple-600 text-white"><th className="text-left px-3 py-2 text-xs">Açıklama</th><th className="text-center px-3 py-2 text-xs">Miktar</th><th className="text-right px-3 py-2 text-xs">Toplam</th></tr></thead>
                  <tbody>{(preview.items || []).map((it, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs">{it.description}</td>
                      <td className="px-3 py-2 text-xs text-center">{it.quantity}</td>
                      <td className="px-3 py-2 text-xs text-right font-semibold">{fmt(it.total || (it.quantity * it.unit_price), preview.currency)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setPreview(null)}>Kapat</Button>
                <Button onClick={() => { downloadPDF(preview); setPreview(null); }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white gap-2">
                  <Printer className="h-4 w-4" />PDF İndir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
